/**
 * @typedef {import('./BlogFilterSearchProps').default} Props
 * @typedef {import('./BlogFilterSearchState').default} State
 */

import { translate } from '../App/Internationalization';
import * as yup from 'yup';
import BaseField from '../shared/Forms/Fields/BaseField';
import BaseForm from '../shared/Forms/BaseForm';
import BlogFilter from './BlogFilter';
import Loading from '../shared/Loading';
import React from 'react';
import SubmitButton from '../shared/Forms/Inputs/SubmitButton';

const validationSchema = yup.object().shape({
	searchQuery: yup.string(),
});

/** @extends {React.Component<Props, State>} */
export default class BlogFilterSearch extends React.Component {
	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);

		this.state = {
			allPostTags: this.props.tagFilterList || [],
			selectedCategory: this.props.selectedCategoryId ?? null,
			selectedTag: this.props.selectedTagId ?? null,
		};

		this.handleSubmit = this.handleSubmit.bind(this);
	}

	/**
	 * @param {Props} prevProps
	 */
	componentDidUpdate(prevProps) {
		this.syncTagFilterList(prevProps);
		this.syncSelectedFilters(prevProps);
	}

	/**
	 * @param {Props} prevProps
	 */
	syncTagFilterList(prevProps) {
		if (prevProps.tagFilterList !== this.props.tagFilterList) {
			this.setState({
				allPostTags: this.props.tagFilterList || [],
			});
		}
	}

	/**
	 * @param {Props} prevProps
	 */
	syncSelectedFilters(prevProps) {
		if (
			prevProps.selectedCategoryId !== this.props.selectedCategoryId ||
			prevProps.selectedTagId !== this.props.selectedTagId
		) {
			this.setState({
				selectedCategory: this.props.selectedCategoryId ?? null,
				selectedTag: this.props.selectedTagId ?? null,
			});
		}
	}

	/**
	 * @param {string} categoryId
	 */
	handleCategoryFilter(categoryId) {
		this.setState(
			(/** @type {{ selectedCategory: string | null; }} */ prev) => ({
				selectedCategory:
					prev.selectedCategory === categoryId ? null : categoryId,
				selectedTag: null,
			}),
			() => this.triggerFilterChange()
		);
	}

	/**
	 * @param {string} tagId
	 */
	handleTagFilter(tagId) {
		this.setState(
			(/** @type {{ selectedTag: string | null; }} */ prev) => ({
				selectedTag: prev.selectedTag === tagId ? null : tagId,
			}),
			() => this.triggerFilterChange()
		);
	}

	/**
	 * @param {{searchQuery: string}} values
	 */
	handleSubmit(values) {
		this.props.onSubmit({
			...values,
			categoryId: this.state.selectedCategory,
			tagId: this.state.selectedTag,
		});
	}

	triggerFilterChange() {
		this.props.onSubmit({
			categoryId: this.state.selectedCategory ?? null,
			searchQuery: this.props.searchQuery ?? '',
			tagId: this.state.selectedTag ?? null,
		});
	}

	/**
	 * @param {JSX.Element[]} additionalFilters
	 * @returns {JSX.Element | JSX.Element[]}
	 */
	renderTagFilters(additionalFilters) {
		if (this.state.allPostTags.length > 0) {
			return additionalFilters;
		}

		return <Loading />;
	}

	/**
	 * @param {JSX.Element[]} additionalFilters
	 * @returns {JSX.Element | null}
	 */
	renderTagSection(additionalFilters) {
		if (this.state.selectedCategory === null) {
			return null;
		}

		return (
			<section className="input-group checkbox-group mt-5">
				<h4 className="hl-ms-1">{this.props.tagTitle ?? 'Tags'}</h4>
				<ul className="button-group my-5 flex flex-wrap items-center gap-x-6 gap-y-3 justify-start">
					{this.renderTagFilters(additionalFilters)}
				</ul>
			</section>
		);
	}

	render() {
		const categoryFilters = this.props.categoryFilterList.map(
			(category) => (
				<BlogFilter
					disabled={false}
					id={`category-filter-${category.tagId}`}
					key={category.tagId}
					label={category.tagName}
					name={'categoryFilter'}
					onChange={() => this.handleCategoryFilter(category.tagId)}
					selected={this.state.selectedCategory === category.tagId}
				/>
			)
		);

		const additionalFilters = this.state.allPostTags.map(
			(/** @type {{tagId: string; tagName: string}} */ tag) => (
				<BlogFilter
					disabled={false}
					id={`tag-filter-${tag.tagId}`}
					key={tag.tagId}
					label={tag.tagName}
					name={'additionalFilter'}
					onChange={() => this.handleTagFilter(tag.tagId)}
					selected={this.state.selectedTag === tag.tagId}
				/>
			)
		);

		return (
			<section className={this.props.className ?? 'w-3/4'}>
				<BaseForm
					className="flex w-full items-end"
					enableReinitialize={true}
					initialValues={{
						searchQuery: this.props.searchQuery ?? '',
					}}
					noRequiredText={true}
					onSubmit={this.handleSubmit}
					validationSchema={validationSchema}
				>
					<BaseField
						className="flex-grow mr-2"
						disabled={this.props.disabled}
						inputClass="h-11"
						labelClass="input-label"
						name="searchQuery"
						onChange={(
							/** @type {{ target: { value: string; }; }} */ event,
							/** @type {{ setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void; submitForm: () => void; }} */ form
						) => {
							form.setFieldValue(
								'searchQuery',
								event.target.value
							);
							if (event.target.value === '') {
								form.submitForm();
							}
						}}
						placeholder={translate(
							'global.blog.forms.search.placeholder'
						)}
						type="search"
					/>

					<SubmitButton disabled={this.props.disabled}>
						{translate('global.forms.buttons.search')}
					</SubmitButton>
				</BaseForm>
				<section className="input-group checkbox-group mt-5">
					<h4 className="hl-ms-1">
						{this.props.categoryTitle ?? 'Categories'}
					</h4>
					<ul className="button-group my-5 flex flex-wrap items-center gap-x-6 gap-y-3 justify-start">
						{categoryFilters}
					</ul>
				</section>
				{this.renderTagSection(additionalFilters)}
			</section>
		);
	}
}
